import React, { useEffect } from 'react';
import loadable from '@loadable/component';
import { useLocation, useHistory } from 'react-router-dom';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useConfiguration } from '../../context/configurationContext';

import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';
import { parse } from '../../util/urlHelpers';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling } from '../../ducks/ui.duck';

import FallbackPage from './FallbackPage';
import { ASSET_NAME } from './LandingPage.duck';

// Import search actions
import { searchListings, setActiveListing } from '../SearchPage/SearchPage.duck';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

export const LandingPageComponent = props => {
  const { 
    pageAssetsData, 
    inProgress, 
    error,
    // Search-related props
    searchParams,
    searchListings,
    searchInProgress,
    listings,
    pagination,
    searchListingsError,
    onManageDisableScrolling,
  } = props;
  
  const location = useLocation();
  const history = useHistory();
  const config = useConfiguration();

  // Note: Scroll function removed since we redirect to search page instead

  // Redirect to search page when filters are applied
  useEffect(() => {
    const urlParams = parse(location.search);
    const hasSearchParams = Object.keys(urlParams).some(key =>
      key.startsWith('pub_categoryLevel') || key === 'keywords' || key === 'address' ||key === 'price' ||
      key.startsWith('pub_sportType') || key.startsWith('pub_brandID') || key.startsWith('pub_sizeid') ||
      key === 'pub_listingType'
    );

    // If search parameters are present, redirect to search page
    if (hasSearchParams) {
      console.log('Redirecting to search page with parameters:', urlParams);
      history.push(`/s${location.search}`);
    } else {
      console.log('Loading all listings (no search parameters)');
      searchListings({}, config); // Empty object to load all listings for initial display
    }
  }, [location.search, searchListings, config, history]);

  // Note: Scroll functionality removed since we redirect to search page instead of showing results here

  // Check if search results should be shown - only show when there are no active filters (initial load)
  const urlParams = parse(location.search);
  const hasActiveFilters = Object.keys(urlParams).some(key =>
    key.startsWith('pub_categoryLevel') || key === 'keywords' || key === 'address' || key === 'price' ||
    key.startsWith('pub_sportType') || key.startsWith('pub_brandID') || key.startsWith('pub_sizeid') ||
    key === 'pub_listingType'
  );

  // Show search results only when there are no active filters (initial listings display)
  const shouldShowSearchResults = !hasActiveFilters && (listings && listings.length >= 0);

  return (
    <PageBuilder
      pageAssetsData={pageAssetsData?.[camelize(ASSET_NAME)]?.data}
      inProgress={inProgress}
      error={error}
      fallbackPage={<FallbackPage error={error} />}
      isLandingPage={true}
      // Pass search-related props to PageBuilder
      showSearchResults={shouldShowSearchResults}
      searchResults={{
        listings,
        pagination,
        searchInProgress,
        searchListingsError,
        searchParams,
        location,
        history,
        onManageDisableScrolling,
      }}
    />
  );
};

LandingPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  
  // Add search-related state
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
  } = state.SearchPage || {};
  
  const listings = getListingsById(state, currentPageResultIds || []);
  
  return { 
    pageAssetsData, 
    inProgress, 
    error,
    // Search state
    searchParams,
    listings,
    pagination,
    searchInProgress,
    searchListingsError,
    scrollingDisabled: state.ui?.isScrollingDisabled || false,
  };
};

const mapDispatchToProps = dispatch => ({
  searchListings: (params, config) => dispatch(searchListings(params, config)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(connect(mapStateToProps, mapDispatchToProps))(LandingPageComponent);

export default LandingPage;
